// Табът „Партиди" на складовата карта: отворените партиди КЪМ МОМЕНТА (без
// период), по реда, в който ще се изписват (FIFO) — приета, партида, срок,
// остатък, цена (нето) и стойност, с ред „Общо". Изтекъл срок е червен, срок в
// следващите 7 дни — кехлибарен. Колоните следват ширината на картата: на
// телефон датата и срокът слизат под партидата, стойността е от 600 px нагоре.
import { Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Lot } from "../../../../api/inventory";
import { formatQty } from "../../components/qty";
import { formatMoney, formatUnitPrice } from "../../components/numbers";
import { fmtDate } from "../../components/dateTime";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
} from "../components/reportListModel";

const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_80px_64px] items-center gap-x-2.5 px-4 @min-[460px]:grid-cols-[76px_minmax(0,1fr)_80px_84px_68px] @min-[600px]:grid-cols-[84px_minmax(0,1fr)_84px_88px_72px_88px]";
const FROM_MEDIUM = "hidden @min-[460px]:block";
const FROM_WIDE = "hidden @min-[600px]:block";
const EXPIRY_WARNING_DAYS = 7;

const expiryTone = (iso: string | null) => {
  if (!iso) return "text-muted";
  const daysLeft = dayjs(iso).startOf("day").diff(dayjs().startOf("day"), "day");
  if (daysLeft < 0) return "font-semibold text-danger-ink";
  if (daysLeft <= EXPIRY_WARNING_DAYS) return "font-semibold text-brand-deep-ink";
  return "text-body";
};

type Props = {
  lots: Lot[];
  isLoading: boolean;
  decimals: number;
  unit: string | null;
};

export const StockCardLots = ({ lots, isLoading, decimals, unit }: Props) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        <Spin />
      </div>
    );
  }
  const totalRemaining = lots.reduce((sum, lot) => sum + lot.remaining, 0);
  const totalValue = lots.reduce((sum, lot) => sum + lot.remaining * lot.unitPrice, 0);

  return (
    <div className="@container flex min-h-0 flex-1 flex-col overflow-auto">
      <p className="m-0 border-b border-shade-2 px-4 py-2.5 text-[12.5px] leading-[1.4] text-body">
        {t("inventoryPage.scLotsHint")}
      </p>
      {lots.length === 0 ? (
        <div className="px-4 py-8 text-center text-[13px] text-body">{t("inventoryPage.scLotsEmpty")}</div>
      ) : (
        <>
          <div className={`${ROW_GRID} ${REPORT_HEADER_ROW_CLASS} sticky top-0 z-[1] bg-panel`}>
            <span className={`${REPORT_HEAD_CLASS} ${FROM_MEDIUM}`}>{t("inventoryPage.scColReceived")}</span>
            <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colBatch")}</span>
            <span className={`${REPORT_HEAD_CLASS} ${FROM_MEDIUM}`}>{t("inventoryPage.colExpiry")}</span>
            <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.scColRemaining")}</span>
            <span className={`${REPORT_HEAD_CLASS} text-right`}>{t("inventoryPage.scColPrice")}</span>
            <span className={`${REPORT_HEAD_CLASS} ${FROM_WIDE} text-right`}>{t("inventoryPage.colValue")}</span>
          </div>
          {lots.map((lot) => (
            <div key={lot.id} className={`${ROW_GRID} ${REPORT_ROW_CLASS}`}>
              <span className={`${FROM_MEDIUM} font-sheet text-xs text-body`}>{fmtDate(lot.date)}</span>
              <span className="flex min-w-0 flex-col gap-px">
                <span className={`truncate ${lot.batchNumber ? "text-ink" : "text-muted"}`}>
                  {lot.batchNumber || t("inventoryPage.noBatch")}
                </span>
                {/* телефон: датата и срокът под партидата */}
                <span className="truncate font-sheet text-[11.5px] text-muted @min-[460px]:hidden">
                  {fmtDate(lot.date)}
                  {lot.expiryDate && (
                    <span className={expiryTone(lot.expiryDate)}> · {fmtDate(lot.expiryDate)}</span>
                  )}
                </span>
              </span>
              <span className={`${FROM_MEDIUM} font-sheet text-xs ${expiryTone(lot.expiryDate)}`}>
                {lot.expiryDate ? fmtDate(lot.expiryDate) : "—"}
              </span>
              <span className="text-right font-sheet font-semibold tabular-nums text-ink">
                {formatQty(lot.remaining, decimals)}
              </span>
              <span className="text-right font-sheet text-xs tabular-nums text-body">
                {formatUnitPrice(lot.unitPrice)}
              </span>
              <span className={`${FROM_WIDE} text-right font-sheet tabular-nums text-ink-2`}>
                {formatMoney(lot.remaining * lot.unitPrice)}
              </span>
            </div>
          ))}
          <div className={`${ROW_GRID} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} col-span-1 @min-[460px]:col-span-3`}>{t("inventoryPage.total")}</span>
            <span className="text-right font-sheet font-bold tabular-nums text-ink">
              {formatQty(totalRemaining, decimals)}
              {unit && <span className="ml-1 font-normal text-muted">{unit}</span>}
            </span>
            <span />
            <span className={`${FROM_WIDE} text-right font-sheet font-bold tabular-nums text-brand-deep-ink`}>
              {formatMoney(totalValue)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default StockCardLots;
